import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  FunnelIcon,
  EyeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { usePagination } from '../hooks/usePagination';
import Pagination from './Pagination';

interface OutboundCallback {
  id: number;
  transactionId: string;
  callbackUrl: string;
  payload: any;
  status: string;
  attempts: number;
  httpStatusCode: number;
  errorMessage: string;
  responseData: any;
  lastAttemptAt: string;
  createdAt: string;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const OutboundCallbacks: React.FC = () => {
  const navigate = useNavigate();
  const [callbacks, setCallbacks] = useState<OutboundCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCallback, setSelectedCallback] = useState<OutboundCallback | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [excludeLocalhost, setExcludeLocalhost] = useState(false);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0, pending: 0 });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const { page, pageSize, setPage, setPageSize } = usePagination();

  const fetchCallbacks = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = statusFilter !== 'all' ? statusFilter : '';
      const response = await axios.get(
        `${API_BASE_URL}/callback/outbound?page=${page}&limit=${pageSize}&status=${statusParam}&excludeLocalhost=${excludeLocalhost}`,
      );
      setCallbacks(response.data.data);
      setTotalItems(response.data.meta.total);
      setTotalPages(response.data.meta.totalPages);
    } catch (error) {
      console.error('Error fetching callbacks:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, statusFilter, excludeLocalhost]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/callback/outbound/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchCallbacks();
    fetchStats();
    const interval = setInterval(() => {
      fetchCallbacks();
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchCallbacks, fetchStats]);

  const handleRetry = async (id: number) => {
    setRetrying(id);
    try {
      await axios.post(`${API_BASE_URL}/callback/outbound/${id}/retry`);
      await fetchCallbacks();
      await fetchStats();
      alert('Callback retried successfully!');
    } catch (error) {
      console.error('Error retrying callback:', error);
      alert('Failed to retry callback');
    } finally {
      setRetrying(null);
    }
  };

  const applyStatusFilter = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      success: 'bg-green-500/15 text-green-300 ring-1 ring-inset ring-green-500/30',
      failed: 'bg-red-500/15 text-red-300 ring-1 ring-inset ring-red-500/30',
      pending: 'bg-yellow-500/15 text-yellow-300 ring-1 ring-inset ring-yellow-500/30',
    };
    return colors[status as keyof typeof colors] || 'bg-slate-700 text-slate-300 ring-1 ring-inset ring-slate-600';
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  const statCards = [
    { name: 'Total', value: stats.total, icon: ArrowPathIcon, color: 'text-slate-400' },
    { name: 'Success', value: stats.success, icon: CheckCircleIcon, color: 'text-green-400' },
    { name: 'Failed', value: stats.failed, icon: ExclamationTriangleIcon, color: 'text-red-400' },
    { name: 'Pending', value: stats.pending, icon: ClockIcon, color: 'text-yellow-400' },
  ];

  const filterButton = (value: string, label: string, activeClass: string) => (
    <button
      onClick={() => applyStatusFilter(value)}
      className={`px-3 py-1 rounded text-sm ${statusFilter === value ? activeClass : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Callbacks</h1>
          <p className="mt-2 text-sm text-slate-400">
            Monitor and retry callbacks sent to external endpoints
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={() => navigate('/callbacks')}
            className="inline-flex items-center px-4 py-2 border border-slate-600 text-sm font-medium rounded-md text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
            View Inbound Callbacks
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-4">
        {statCards.map((item) => (
          <div key={item.name} className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <item.icon className={`h-6 w-6 ${item.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-400 truncate">{item.name}</dt>
                    <dd className="text-lg font-semibold text-slate-100">{item.value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <FunnelIcon className="h-5 w-5 text-slate-400" />
          <span className="text-sm font-medium text-slate-300">Filter:</span>
          {filterButton('all', `All (${stats.total})`, 'bg-indigo-600 text-white')}
          {filterButton('success', `Success (${stats.success})`, 'bg-green-600 text-white')}
          {filterButton('failed', `Failed (${stats.failed})`, 'bg-red-600 text-white')}
          {filterButton('pending', `Pending (${stats.pending})`, 'bg-yellow-600 text-white')}
          <div className="ml-auto flex items-center">
            <input
              type="checkbox"
              id="excludeLocalhost"
              checked={excludeLocalhost}
              onChange={(e) => {
                setExcludeLocalhost(e.target.checked);
                setPage(1);
              }}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-600 bg-slate-700 rounded"
            />
            <label htmlFor="excludeLocalhost" className="ml-2 text-sm text-slate-300">
              Hide Localhost
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-700 bg-slate-800 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Transaction ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">URL</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">HTTP Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Attempts</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Last Attempt</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {callbacks.map((callback) => (
                  <tr key={callback.id} className="hover:bg-slate-700/40 transition-colors">
                    <td className="px-6 py-4 text-sm text-slate-200">{callback.transactionId}</td>
                    <td className="px-6 py-4 text-sm text-slate-400 truncate max-w-xs" title={callback.callbackUrl}>
                      {callback.callbackUrl}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(callback.status)}`}>
                        {callback.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-200">{callback.httpStatusCode || '-'}</td>
                    <td className="px-6 py-4 text-sm text-slate-200">{callback.attempts}</td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {callback.lastAttemptAt ? formatDate(callback.lastAttemptAt) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm space-x-2">
                      <button
                        onClick={() => setSelectedCallback(callback)}
                        className="text-indigo-400 hover:text-indigo-300"
                      >
                        <EyeIcon className="h-5 w-5 inline" /> View
                      </button>
                      {callback.status === 'failed' && (
                        <button
                          onClick={() => handleRetry(callback.id)}
                          disabled={retrying === callback.id}
                          className="text-green-400 hover:text-green-300 disabled:text-slate-500"
                        >
                          {retrying === callback.id ? (
                            <span className="inline-flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-400 mr-1"></div>
                              Retrying...
                            </span>
                          ) : (
                            <>
                              <ArrowPathIcon className="h-5 w-5 inline" /> Retry
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {callbacks.length === 0 && (
              <div className="text-center py-12">
                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-slate-500" />
                <h3 className="mt-2 text-sm font-medium text-slate-200">No callbacks</h3>
                <p className="mt-1 text-sm text-slate-400">
                  {statusFilter === 'all' ? 'No callbacks have been sent yet.' : `No ${statusFilter} callbacks found.`}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {/* Detail Modal */}
      {selectedCallback && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/60 transition-opacity" onClick={() => setSelectedCallback(null)}></div>
            <div className="inline-block align-bottom bg-slate-800 border border-slate-700 rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg leading-6 font-medium text-slate-100">
                    Callback Details #{selectedCallback.id}
                  </h3>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(selectedCallback.status)}`}>
                    {selectedCallback.status}
                  </span>
                </div>
                <div className="space-y-4">
                  <div>
                    <strong className="text-sm text-slate-400">Transaction ID:</strong>
                    <p className="text-sm text-slate-200 font-mono">{selectedCallback.transactionId}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Callback URL:</strong>
                    <p className="text-sm text-slate-200 break-all">{selectedCallback.callbackUrl}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Status:</strong>
                    <p className="text-sm text-slate-200">{selectedCallback.status}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">HTTP Status Code:</strong>
                    <p className={`text-sm font-bold ${selectedCallback.httpStatusCode >= 200 && selectedCallback.httpStatusCode < 300 ? 'text-green-400' : 'text-red-400'}`}>
                      {selectedCallback.httpStatusCode || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Attempts:</strong>
                    <p className="text-sm text-slate-200">{selectedCallback.attempts}</p>
                  </div>
                  {selectedCallback.errorMessage && (
                    <div>
                      <strong className="text-sm text-red-300">Error Message:</strong>
                      <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 p-3 rounded mt-1">{selectedCallback.errorMessage}</p>
                    </div>
                  )}
                  <div>
                    <strong className="text-sm text-slate-400">Payload Sent:</strong>
                    <pre className="mt-2 text-xs bg-slate-900 text-slate-200 p-4 rounded-lg overflow-x-auto">
                      {JSON.stringify(selectedCallback.payload, null, 2)}
                    </pre>
                  </div>
                  {selectedCallback.responseData && (
                    <div>
                      <strong className="text-sm text-slate-400">Response Data:</strong>
                      <pre className="mt-2 text-xs bg-slate-900 text-slate-200 p-4 rounded-lg overflow-x-auto">
                        {JSON.stringify(selectedCallback.responseData, null, 2)}
                      </pre>
                    </div>
                  )}
                  <div>
                    <strong className="text-sm text-slate-400">Last Attempt:</strong>
                    <p className="text-sm text-slate-200">{selectedCallback.lastAttemptAt ? formatDate(selectedCallback.lastAttemptAt) : '-'}</p>
                  </div>
                  <div>
                    <strong className="text-sm text-slate-400">Created:</strong>
                    <p className="text-sm text-slate-200">{formatDate(selectedCallback.createdAt)}</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 flex justify-end space-x-3">
                {selectedCallback.status === 'failed' && (
                  <button
                    type="button"
                    onClick={() => {
                      handleRetry(selectedCallback.id);
                      setSelectedCallback(null);
                    }}
                    className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-green-500 sm:text-sm"
                  >
                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                    Retry Now
                  </button>
                )}
                <button
                  type="button"
                  className="inline-flex justify-center rounded-md border border-slate-600 shadow-sm px-4 py-2 bg-slate-800 text-base font-medium text-slate-200 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-indigo-500 sm:text-sm"
                  onClick={() => setSelectedCallback(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboundCallbacks;
